import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { IconButton, InputAdornment, Stack } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import Tooltip from '@mui/material/Tooltip';
import ClearIcon from '@mui/icons-material/Clear';
import Divider from '@mui/material/Divider';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import CodeInput from 'components/inputs/CodeInput';
import InlineIconSelector from 'components/InlineIconSelector';
import ColorPicker from 'components/ColorPicker';

/**
 * Component for displaying and managing the details of an prompt. Allows viewing,
 * creating, and updating prompts.
 *
 * @component
 *
 * @example
 * <PromptDetails prompt={prompt} onSubmit={handleSubmit} />
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.knowledgeBase - The API key object to be displayed and edited.
 * @param {Function} props.onSubmit - The function to call when the form is submitted.
 * @param {Function} props.onFieldChange - The function to call when the fields value change.
 *
 * @returns {React.Component} The API Key Details component.
 */
function KnowledgeBaseDetails({ knowledgeBase, onSubmit, onFieldChange }) {
  const { formatMessage: msg } = useIntl();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty }
  } = useForm({
    defaultValues: {
      id: '',
      code: '',
      label: '',
      description: '',
      icon: 'school',
      color: '#9c27b0'
    }
  });

  watch();

  useEffect(() => {
    if (onFieldChange) {
      onFieldChange(isDirty);
    }
  }, [isDirty, onFieldChange]);

  const resetKnowledgeBase = useCallback(() => {
    if (knowledgeBase?.id) {
      reset(knowledgeBase);
    } else {
      reset({
        id: '',
        code: '',
        label: '',
        description: '',
        icon: 'school',
        color: '#9c27b0'
      });
    }
  }, [knowledgeBase, reset]);

  useEffect(() => {
    resetKnowledgeBase();
  }, [knowledgeBase, resetKnowledgeBase]);

  const clearField = (fieldName) => {
    setValue(fieldName, '');
  };

  const handleReset = () => {
    resetKnowledgeBase();
  };

  const isCreating = !knowledgeBase?.id;

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      display="flex"
      direction="column"
      sx={{ height: '100%' }}
    >
      <Stack display="flex" direction="row">
        <SpeakerNotesIcon color="text" fontSize="medium" sx={{ mr: '0.75rem' }} />
        <MDTypography variant="h5">
          {msg({
            id: isCreating
              ? 'page.knowledgeBase.details.creation.title'
              : 'page.knowledgeBase.details.modification.title'
          })}
        </MDTypography>
      </Stack>
      <Divider sx={{ mt: '0.5rem', mb: '0.75rem' }} />
      <Stack
        spacing={1}
        display="flex"
        direction="column"
        flex={1}
        overflow="auto"
        mb={2}
        sx={{ scrollbarWidth: 'thin' }}
      >
        {!isCreating && (
          <Controller
            name="id"
            control={control}
            render={({ field }) => (
              <MDInput
                {...field}
                label={msg({ id: 'field.id.label' })}
                variant="filled"
                fullWidth
                required
                size="small"
                InputProps={{
                  readOnly: true
                }}
              />
            )}
          />
        )}

        <Controller
          name="code"
          control={control}
          rules={{ required: msg({ id: 'field.code.required' }) }}
          render={({ field }) => (
            <CodeInput field={field} errors={errors} clearField={() => clearField('code')} />
          )}
        />

        <Controller
          name="label"
          control={control}
          rules={{ required: msg({ id: 'field.label.required' }) }}
          render={({ field }) => (
            <MDInput
              required
              {...field}
              label={msg({ id: 'field.label.label' })}
              variant="filled"
              fullWidth
              size="small"
              error={!!errors.label}
              helperText={errors.label ? errors.label.message : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('label')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.description.label' })}
              variant="filled"
              fullWidth
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('description')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Controller
          name="icon"
          control={control}
          render={({ field }) => (
            <InlineIconSelector
              value={field.value}
              onChange={(iconName) => field.onChange(iconName)}
            />
          )}
        />
        <Controller
          name="color"
          control={control}
          render={({ field }) => (
            <ColorPicker
              value={field.value}
              onChange={(color) => field.onChange(color)}
              label={msg({ id: 'field.color.label' })}
            />
          )}
        />
      </Stack>
      <Divider sx={{ mt: 0 }} />
      <Stack direction="row" spacing={2} sx={{ marginTop: 'auto !important' }}>
        <MDButton type="button" fullWidth color="secondary" onClick={handleReset}>
          {msg({ id: 'button.reset' })}
        </MDButton>
        <MDButton type="submit" fullWidth color="primary">
          {isCreating ? msg({ id: 'button.create' }) : msg({ id: 'button.update' })}
        </MDButton>
      </Stack>
    </Stack>
  );
}

KnowledgeBaseDetails.propTypes = {
  knowledgeBase: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
    label: PropTypes.string,
    description: PropTypes.string,
    color: PropTypes.string,
    icon: PropTypes.string
  }),
  onSubmit: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired
};

export default KnowledgeBaseDetails;
